import {
  BookmarkIcon,
  ClockIcon,
  ListIcon,
  MusicIcon,
  SettingsIcon,
  TerminalIcon,
} from '@streamersonglist/shared-remix-ui-icon';
import type { TFunction } from 'i18next';

export const ACTION_LOG_TYPES = [
  'song',
  'queue',
  'play_history',
  'commands',
  'settings',
  'saved_queue',
] as const;
export type ActionLogType = (typeof ACTION_LOG_TYPES)[number];

export const typeIcons: Record<ActionLogType, React.FC<React.SVGProps<SVGSVGElement>>> = {
  song: MusicIcon,
  queue: ListIcon,
  play_history: ClockIcon,
  commands: TerminalIcon,
  settings: SettingsIcon,
  saved_queue: BookmarkIcon,
};

/**
 * Every `type`/`action` pair the API writes to the action log. Each one owns a
 * whole-sentence translation key, because a verb glued onto a noun only reads
 * as a sentence in English.
 */
export const ACTION_LOG_PAIRS: ReadonlyArray<[ActionLogType, string]> = [
  ['song', 'create'],
  ['song', 'update'],
  ['song', 'delete'],
  ['song', 'bulk-update'],
  ['song', 'bulk-delete'],
  ['queue', 'create'],
  ['queue', 'update'],
  ['queue', 'delete'],
  ['queue', 'clear'],
  ['queue', 'play'],
  ['queue', 'promote'],
  ['queue', 'clear_playing'],
  ['queue', 'save_to_saved'],
  ['queue', 'queue_from_saved'],
  ['play_history', 'create'],
  ['play_history', 'update'],
  ['play_history', 'delete'],
  ['commands', 'create'],
  ['commands', 'update'],
  ['commands', 'delete'],
  ['commands', 'enable'],
  ['commands', 'disable'],
  ['settings', 'update'],
  ['saved_queue', 'create'],
  ['saved_queue', 'update'],
  ['saved_queue', 'delete'],
  ['saved_queue', 'save_from_queue'],
  ['saved_queue', 'queue_from_saved'],
];

const REGISTERED_PAIRS = new Set(ACTION_LOG_PAIRS.map(([type, action]) => `${type}:${action}`));

/** Pairs whose payload carries a `count`, so their keys are pluralised. */
const COUNTED_PAIRS = new Set(['song:bulk-update', 'song:bulk-delete', 'queue:clear']);

export function typeLabelKey(type: string): string {
  return `activity.type.${type}`;
}

function readString(data: unknown, key: string): string | undefined {
  if (data == null || typeof data !== 'object') return undefined;
  const value = (data as Record<string, unknown>)[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function readNumber(data: unknown, key: string): number | undefined {
  if (data == null || typeof data !== 'object') return undefined;
  const value = (data as Record<string, unknown>)[key];
  return typeof value === 'number' ? value : undefined;
}

type ActionLogMessage = {
  action: string;
  type: string;
  user: string;
  data?: unknown;
};

export function formatActionLogMessage(
  t: TFunction,
  { action, type, user, data }: ActionLogMessage,
): string {
  const pair = `${type}:${action}`;
  if (!REGISTERED_PAIRS.has(pair)) {
    return t('activity.msg.generic', { user, action, type });
  }

  // `:` is i18next's namespace separator, so the pair is joined with a dot.
  const base = `activity.msg.${type}.${action.replace(/-/g, '_')}`;
  const song = readString(data, 'songTitle') ?? t('activity.msg.a_song');
  const command = readString(data, 'commandName') ?? t('activity.msg.a_command');
  const count = readNumber(data, 'count');

  if (!COUNTED_PAIRS.has(pair)) {
    return t(base, { user, song, command });
  }

  return count == null ? t(`${base}_unknown`, { user }) : t(base, { user, count });
}
